// client/src/dnd/views/Interactive_parts/TeleportManager.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useMap } from "react-leaflet";

function pointInPolygon(pt, polygon) {
  let x = pt.x,
    y = pt.y,
    inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y;
    const xj = polygon[j].x,
      yj = polygon[j].y;
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function navigateToTeleportTarget(rawTarget) {
  const t = (rawTarget || "").toString().trim();
  if (!t) return;

  if (/^https?:\/\//i.test(t)) {
    window.location.assign(t);
    return;
  }

  if (t.startsWith("/")) {
    const idMatch = t.match(/(\d+)(?!.*\d)/);
    if (idMatch) {
      const id = idMatch[1];
      window.location.assign(`/vtt/${id}`);
      return;
    }
    window.location.assign(t);
    return;
  }

  const mapMatch = t.match(/^(map=)?(\d+)$/i) || t.match(/^(\d+)$/);
  if (mapMatch) {
    const id = mapMatch[2] || mapMatch[1];
    window.location.assign(`/vtt/${id}`);
    return;
  }
}

function parseGeometry(geometry) {
  let geom = geometry;
  if (typeof geom === "string") {
    try {
      geom = JSON.parse(geom);
    } catch {
      return null;
    }
  }
  if (!geom || typeof geom !== "object") return null;
  if (geom.type === "Feature" && geom.geometry) {
    geom = geom.geometry;
  }
  if (
    geom.type === "Polygon" &&
    Array.isArray(geom.coordinates) &&
    geom.coordinates[0]
  ) {
    return geom.coordinates[0].map(([lng, lat]) => ({
      x: Number(lng),
      y: Number(lat),
    }));
  }
  if (
    geom.type === "MultiPolygon" &&
    Array.isArray(geom.coordinates) &&
    geom.coordinates[0] &&
    geom.coordinates[0][0]
  ) {
    return geom.coordinates[0][0].map(([lng, lat]) => ({
      x: Number(lng),
      y: Number(lat),
    }));
  }
  return null;
}

function normalizeTargetValue(value) {
  if (value == null) return null;
  if (typeof value === "number") return `/map/${value}`;
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return null;
    if (/^https?:\/\//i.test(s)) return s;
    if (/^\//.test(s)) return s;
    if (/^\d+$/.test(s)) return `/map/${s}`;
    return s;
  }
  if (typeof value === "object") {
    if (typeof value.url === "string") return value.url.trim();
    if (typeof value.href === "string") return value.href.trim();
    if (typeof value.path === "string") return value.path.trim();
    if (value.id != null) {
      const id = String(value.id);
      if (/^\d+$/.test(id)) return `/map/${id}`;
    }
  }
  return null;
}

function safeParseJsonMaybeString(v) {
  if (v == null) return {};
  if (typeof v === "object") return v;
  if (typeof v === "string") {
    try {
      const o = JSON.parse(v);
      return typeof o === "object" && o ? o : {};
    } catch {
      return {};
    }
  }
  return {};
}

function extractTargetFromRow(row) {
  const bpRaw = row?.behaviorProps ?? row?.behavior_props ?? "{}";
  const bsRaw = row?.behaviorState ?? row?.behavior_state ?? "{}";
  const bp = safeParseJsonMaybeString(bpRaw);
  const bs = safeParseJsonMaybeString(bsRaw);
  const candidates = [
    bp.target,
    bp.teleportTarget,
    bp.goto,
    bp.to,
    bp.url,
    bp.href,
    bp.path,
    bp.link,
    bp.map,
    bp.mapId,
    bp.map_id,
    bp.targetMapId,
    bp.target_map_id,
    bp.childMapId,
    bp.child_map_id,
    bp.destinationMapId,
    bp.destination_map_id,
    bp.destination,
    bs.target,
    bs.teleportTarget,
    bs.goto,
    bs.to,
    bs.url,
    bs.href,
    bs.path,
    bs.link,
    bs.map,
    bs.mapId,
    bs.map_id,
    bs.targetMapId,
    bs.target_map_id,
    bs.childMapId,
    bs.child_map_id,
    bs.destinationMapId,
    bs.destination_map_id,
    bs.destination,
  ];
  for (const c of candidates) {
    const v = normalizeTargetValue(c);
    if (v) return v;
  }
  const label = (row?.label || "").toString().trim();
  if (/^teleport:/i.test(label)) {
    const v = label.replace(/^teleport:\s*/i, "").trim();
    const n = normalizeTargetValue(v);
    return n || v;
  }
  return null;
}

function getRandomTeleportMessage() {
  const msgs = [
    "The way forward lies here. Continue?",
    "A passage awaits — will you take it?",
    "The next step leads to unfamiliar ground. Proceed?",
    "You’ve reached a point of decision. Move on?",
    "The route ahead is open. Enter?",
    "This way leads elsewhere. Shall we go?",
    "A crossing lies before you. Take it?",
    "Beyond this lies a new place. Step through?",
    "Your path turns here. Advance?",
    "You stand at the threshold. Proceed?",
    "The journey continues if you wish it. Move forward?",
    "This exit leads to another area. Use it?",
    "The choice is yours: stay or go?",
    "This point marks a change in your path. Continue?",
    "The door ahead stands ready. Open it?",
    "This path will take you onward. Travel now?",
    "Only a step stands between you and what’s next. Cross?",
    "Ahead lies the next chapter. Shall we turn the page?",
    "You’ve found a way out. Use it?",
    "The destination awaits. Will you go there?",
  ];
  return msgs[Math.floor(Math.random() * msgs.length)];
}

function resetGeomanModes(map) {
  if (!map || !map.pm) return;
  try {
    map.pm.disableDraw();
  } catch {}
  try {
    map.pm.disableGlobalEditMode();
  } catch {}
  try {
    map.pm.disableGlobalDragMode();
  } catch {}
  try {
    map.pm.disableGlobalRemovalMode();
  } catch {}
  try {
    map.pm.disableGlobalCutMode && map.pm.disableGlobalCutMode();
  } catch {}
}

function releaseGlobalPointer() {
  try {
    const mouseUp = new MouseEvent("mouseup", {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(mouseUp);
    document.dispatchEvent(mouseUp);
  } catch {}

  try {
    const pointerUp = new PointerEvent("pointerup", {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(pointerUp);
    document.dispatchEvent(pointerUp);
  } catch {}
}

function toFiniteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeSpeechZone(row) {
  const polygon = parseGeometry(row.geometry);
  if (!polygon || polygon.length < 3) return null;

  const behaviorProps = safeParseJsonMaybeString(
    row.behaviorProps ?? row.behavior_props
  );
  const behaviorState = safeParseJsonMaybeString(
    row.behaviorState ?? row.behavior_state
  );

  const reverbAmount = Math.max(
    0,
    Math.min(10, toFiniteNumber(behaviorProps.reverbAmount, 1))
  );
  const reverbCharacter =
    String(behaviorProps.reverbCharacter || "medium").trim().toLowerCase() ||
    "medium";
  const compressionAmount = Math.max(
    0,
    Math.min(10, toFiniteNumber(behaviorProps.compressionAmount, 0))
  );
  const brightnessAmount = Math.max(
    0,
    Math.min(10, toFiniteNumber(behaviorProps.brightnessAmount, 5))
  );

  return {
    ...row,
    id: row.id ?? null,
    mapId: row.mapId ?? row.map_id ?? null,
    behaviorType: row.behaviorType || row.behavior_type || null,
    strokeOpacity: row.strokeOpacity ?? row.stroke_opacity,
    fillOpacity: row.fillOpacity ?? row.fill_opacity,
    behaviorProps,
    behaviorState,
    polygon,
    speech: {
      reverbAmount,
      reverbCharacter,
      compressionAmount,
      brightnessAmount,
    },
  };
}

function buildSpeechZonePayload(zone) {
  if (!zone) return null;
  return {
    shapeId: zone.id ?? null,
    behaviorType: "speech_effects",
    behaviorProps: {
      reverbAmount: zone.speech?.reverbAmount ?? 1,
      reverbCharacter: zone.speech?.reverbCharacter || "medium",
      compressionAmount: zone.speech?.compressionAmount ?? 0,
      brightnessAmount: zone.speech?.brightnessAmount ?? 5,
    },
  };
}

function dispatchLocalSpeechZone(zone) {
  const payload = buildSpeechZonePayload(zone);

  try {
    window.dispatchEvent(
      new CustomEvent("pa-local-speech-zone", {
        detail: {
          zone: payload,
        },
      })
    );
  } catch {}

  const message = {
    type: "pa-local-speech-zone",
    zone: payload,
  };

  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, "*");
    }
  } catch {}

  try {
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, "*");
    }
  } catch {}
}

function getSpeechZoneSignature(zone) {
  if (!zone) return "";
  try {
    return JSON.stringify({
      shapeId: zone.id ?? null,
      reverbAmount: zone.speech?.reverbAmount ?? 1,
      reverbCharacter: zone.speech?.reverbCharacter || "medium",
      compressionAmount: zone.speech?.compressionAmount ?? 0,
      brightnessAmount: zone.speech?.brightnessAmount ?? 5,
    });
  } catch {
    return `${String(zone.id ?? "")}:${String(
      zone.speech?.reverbAmount ?? 1
    )}:${String(zone.speech?.reverbCharacter || "medium")}:${String(
      zone.speech?.compressionAmount ?? 0
    )}:${String(zone.speech?.brightnessAmount ?? 5)}`;
  }
}

function pickWinningSpeechZone(matches) {
  if (!Array.isArray(matches) || matches.length === 0) return null;
  return matches[matches.length - 1] || null;
}

function buildZonesFromRows(rows) {
  const nextTeleportZones = [];
  const nextSpeechZones = [];

  for (const row of Array.isArray(rows) ? rows : []) {
    const behaviorType = (row.behaviorType || row.behavior_type || "")
      .toString()
      .trim()
      .toLowerCase();

    if (behaviorType === "teleport") {
      const polygon = parseGeometry(row.geometry);
      if (!polygon || polygon.length < 3) continue;
      const target = extractTargetFromRow(row);
      if (!target) continue;

      nextTeleportZones.push({
        ...row,
        behaviorType: row.behaviorType || row.behavior_type || null,
        strokeOpacity: row.strokeOpacity ?? row.stroke_opacity,
        fillOpacity: row.fillOpacity ?? row.fill_opacity,
        behaviorProps: safeParseJsonMaybeString(
          row.behaviorProps ?? row.behavior_props
        ),
        behaviorState: safeParseJsonMaybeString(
          row.behaviorState ?? row.behavior_state
        ),
        polygon,
        target,
      });
      continue;
    }

    if (behaviorType === "speech_effects") {
      const normalized = normalizeSpeechZone(row);
      if (normalized) nextSpeechZones.push(normalized);
    }
  }

  return {
    teleportZones: nextTeleportZones,
    speechZones: nextSpeechZones,
  };
}

export default function TeleportManager({ mapId, tokens, hardcodedMarker }) {
  const map = useMap();
  const [teleportZones, setTeleportZones] = useState([]);
  const [speechZones, setSpeechZones] = useState([]);
  const [prompt, setPrompt] = useState(null);
  const suppressRef = useRef(new Map());
  const activeSpeechSignatureRef = useRef("");

  const evaluateSpeechZones = useCallback(
    zones => {
      const playerTokens = Array.isArray(tokens)
        ? tokens.filter(t => {
            const type = (t?.type || "").toString().toLowerCase();
            return type === "player";
          })
        : [];

      const points = playerTokens.map(t => {
        const tx = typeof t.x === "number" ? t.x : parseFloat(t.x);
        const ty = typeof t.y === "number" ? t.y : parseFloat(t.y);
        return {
          x: tx,
          y: ty,
          key: t.id != null ? `token:${t.id}` : `token:${tx},${ty}`,
        };
      });

      if (
        hardcodedMarker &&
        hardcodedMarker.x != null &&
        hardcodedMarker.y != null
      ) {
        const hx =
          typeof hardcodedMarker.x === "number"
            ? hardcodedMarker.x
            : parseFloat(hardcodedMarker.x);
        const hy =
          typeof hardcodedMarker.y === "number"
            ? hardcodedMarker.y
            : parseFloat(hardcodedMarker.y);
        if (isFinite(hx) && isFinite(hy)) {
          points.push({ x: hx, y: hy, key: "hardcoded" });
        }
      }

      if (!zones.length || !points.length) {
        if (activeSpeechSignatureRef.current !== "") {
          activeSpeechSignatureRef.current = "";
          dispatchLocalSpeechZone(null);
        }
        return;
      }

      const matches = [];
      for (const pt of points) {
        if (!isFinite(pt.x) || !isFinite(pt.y)) continue;
        for (const zone of zones) {
          if (pointInPolygon({ x: pt.x, y: pt.y }, zone.polygon)) {
            matches.push(zone);
          }
        }
      }

      const winningZone = pickWinningSpeechZone(matches);
      const nextSignature = getSpeechZoneSignature(winningZone);

      if (nextSignature !== activeSpeechSignatureRef.current) {
        activeSpeechSignatureRef.current = nextSignature;
        dispatchLocalSpeechZone(winningZone);
      }
    },
    [tokens, hardcodedMarker]
  );

  const loadZones = useCallback(async () => {
    if (!mapId) {
      setTeleportZones([]);
      setSpeechZones([]);
      if (activeSpeechSignatureRef.current !== "") {
        activeSpeechSignatureRef.current = "";
        dispatchLocalSpeechZone(null);
      }
      return;
    }

    try {
      const res = await fetch(`/api/polygons?mapId=${encodeURIComponent(mapId)}`);
      const rows = await res.json();
      const next = buildZonesFromRows(rows);

      setTeleportZones(next.teleportZones);
      setSpeechZones(next.speechZones);
      evaluateSpeechZones(next.speechZones);
    } catch {
      setTeleportZones([]);
      setSpeechZones([]);
      if (activeSpeechSignatureRef.current !== "") {
        activeSpeechSignatureRef.current = "";
        dispatchLocalSpeechZone(null);
      }
    }
  }, [mapId, evaluateSpeechZones]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (cancelled) return;
      await loadZones();
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [loadZones]);

  useEffect(() => {
    function handlePolygonsChanged(e) {
      const changedMapId = e?.detail?.mapId;
      if (changedMapId != null && String(changedMapId) !== String(mapId)) return;
      loadZones();
    }

    window.addEventListener("pa-polygons-changed", handlePolygonsChanged);

    return () => {
      window.removeEventListener("pa-polygons-changed", handlePolygonsChanged);
    };
  }, [mapId, loadZones]);

  useEffect(() => {
    if (teleportZones.length === 0) return;
    if (prompt) return;

    const playerTokens = Array.isArray(tokens)
      ? tokens.filter(t => {
          const type = (t?.type || "").toString().toLowerCase();
          return type === "player";
        })
      : [];

    const points = playerTokens.map(t => {
      const tx = typeof t.x === "number" ? t.x : parseFloat(t.x);
      const ty = typeof t.y === "number" ? t.y : parseFloat(t.y);
      return {
        x: tx,
        y: ty,
        key: t.id != null ? `token:${t.id}` : `token:${tx},${ty}`,
      };
    });

    if (
      hardcodedMarker &&
      hardcodedMarker.x != null &&
      hardcodedMarker.y != null
    ) {
      const hx =
        typeof hardcodedMarker.x === "number"
          ? hardcodedMarker.x
          : parseFloat(hardcodedMarker.x);
      const hy =
        typeof hardcodedMarker.y === "number"
          ? hardcodedMarker.y
          : parseFloat(hardcodedMarker.y);
      if (isFinite(hx) && isFinite(hy)) {
        points.push({ x: hx, y: hy, key: "hardcoded" });
      }
    }

    if (points.length === 0) return;

    const now = Date.now();
    for (const pt of points) {
      if (!isFinite(pt.x) || !isFinite(pt.y)) continue;
      for (const z of teleportZones) {
        const supKey = `${pt.key}|${z.id}`;
        const until = suppressRef.current.get(supKey);
        if (until && until > now) continue;
        if (pointInPolygon({ x: pt.x, y: pt.y }, z.polygon)) {
          resetGeomanModes(map);
          releaseGlobalPointer();

          setPrompt({
            zone: z,
            target: z.target,
            message: getRandomTeleportMessage(),
            supKey,
          });
          return;
        }
      }
    }
  }, [tokens, hardcodedMarker, teleportZones, prompt, map]);

  useEffect(() => {
    evaluateSpeechZones(speechZones);
  }, [speechZones, evaluateSpeechZones]);

  useEffect(() => {
    return () => {
      activeSpeechSignatureRef.current = "";
      dispatchLocalSpeechZone(null);
    };
  }, []);

  function onConfirm(e) {
    if (e) e.stopPropagation();
    releaseGlobalPointer();
    const p = prompt;
    setPrompt(null);
    if (p && p.target) {
      navigateToTeleportTarget(p.target);
    }
  }

  function onCancel(e) {
    if (e) e.stopPropagation();
    releaseGlobalPointer();
    const p = prompt;
    setPrompt(null);
    if (p && p.supKey) {
      suppressRef.current.set(p.supKey, Date.now() + 5000);
    }
  }

  function stopMouseDownAndClick(e) {
    e.stopPropagation();
  }

  return (
    <>
      {prompt && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "#0008",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
          }}
          onMouseDown={stopMouseDownAndClick}
          onClick={stopMouseDownAndClick}
        >
          <div
            style={{
              background: "#23242a",
              color: "#fff",
              padding: 20,
              borderRadius: 12,
              minWidth: 300,
              maxWidth: 420,
              boxShadow: "0 8px 30px #000c",
              border: "1px solid #444",
            }}
            onMouseDown={stopMouseDownAndClick}
            onClick={stopMouseDownAndClick}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                marginBottom: 8,
              }}
            >
              {prompt.message}
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#ccc",
                marginBottom: 16,
              }}
            >
              You’ve reached a transition zone. Do you want to move to the
              linked area now?
            </div>
            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
              }}
            >
              <button
                onMouseDown={stopMouseDownAndClick}
                onClick={onCancel}
                style={{
                  background: "transparent",
                  color: "#fff",
                  border: "1px solid #777",
                  padding: "6px 14px",
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Stay Here
              </button>
              <button
                onMouseDown={stopMouseDownAndClick}
                onClick={onConfirm}
                style={{
                  background: "#4cc153",
                  color: "#111",
                  border: "none",
                  padding: "6px 16px",
                  borderRadius: 8,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Move
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}